import { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, X, ShieldCheck, User } from "lucide-react";
import { api } from "@/lib/api";

// Merchant → deductible-hint keyword map. Kept alongside the modal so both the
// UI and any downstream tooling (tests, storybook, background heuristics) can
// pull from one source of truth.
export const BUSINESS_KEYWORD_MAP = [
  {
    rx: /\b(office\s?(depot|max)?|staples|paper|toner|ink|supply|supplies)\b/i,
    tag: "office supplies",
    tip: "IRS Line 22 · Supplies — office consumables used for work are typically fully deductible.",
  },
  {
    rx: /\b(adobe|figma|notion|slack|zoom|github|aws|azure|gcp|dropbox|1password|stripe|quickbooks|xero|salesforce|hubspot|software|saas|subscription)\b/i,
    tag: "software",
    tip: "IRS Line 48 · Other Expenses — SaaS + software subscriptions used for work are deductible in full.",
  },
  {
    rx: /\b(uber|lyft|delta|united|american air|southwest|jetblue|amtrak|marriott|hilton|hyatt|hertz|avis|airbnb|expedia|kayak|priceline|travel|flight|hotel)\b/i,
    tag: "business travel",
    tip: "IRS Line 24a · Travel — flights, lodging, and rideshare for work qualify as full write-offs (with substantiation).",
  },
];

export function matchBusinessKeyword(merchant) {
  if (!merchant) return null;
  for (const m of BUSINESS_KEYWORD_MAP) if (m.rx.test(merchant)) return m;
  return null;
}

// Shared queue helper — used by BOTH the automated fresh-receipts effect AND
// the manual `loophole-triage-open` CustomEvent bridge so the two ingestion
// streams cannot drift apart again (regression protection for iteration_23).
function enqueueCandidate({ receipt, candidate, setCandidate, setQueue }) {
  if (!receipt || !receipt.id) return;
  if (!candidate) setCandidate(receipt);
  else setQueue((prev) => [...prev, receipt]);
}

/**
 * LoopholeTriageModal
 * -------------------
 * Watches `recent` receipts (poll-driven) for newly ingested items that need a
 * quick business-vs-personal decision. Presents a compact glassmorphism modal
 * with:
 *   • Merchant + amount + date
 *   • Business / Personal one-tap classification
 *   • AI-Insight prompt (gold pulse when we spot a strong deductible pattern)
 *   • Bulk Triage Queue chip (auto-advances the modal through a backlog)
 *   • 5-second Sonner "Undo" toast on each classification
 *
 * Also listens for a `loophole-triage-open` CustomEvent (`e.detail = receipt`)
 * so tests and other UI surfaces (Receipts page "Re-review", etc.) can drive
 * the modal without waiting for the poll.
 */
export function LoopholeTriageModal({ recent }) {
  const [candidate, setCandidate] = useState(null);
  const [queue, setQueue] = useState([]);
  const seenRef = useRef(null);
  const dismissedRef = useRef(new Set());

  // Stable enqueue helper closure that the manual event bridge can call.
  const enqueue = useCallback(
    (r) => enqueueCandidate({ receipt: r, candidate, setCandidate, setQueue }),
    [candidate]
  );

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem("rs.triage.seen");
      const arr = raw ? JSON.parse(raw) : [];
      seenRef.current = new Set(Array.isArray(arr) ? arr : []);
    } catch (_) {
      seenRef.current = new Set();
    }
    try {
      const raw = window.localStorage.getItem("rs.triage.dismissed");
      const arr = raw ? JSON.parse(raw) : [];
      if (Array.isArray(arr)) dismissedRef.current = new Set(arr);
    } catch (_) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!recent || seenRef.current === null) return;
    // First observed sync on a fresh browser — seed silently so we don't nag
    // the user about receipts that already existed before install.
    if (seenRef.current.size === 0 && recent.length > 0) {
      recent.forEach((r) => seenRef.current.add(r.id));
      try {
        window.localStorage.setItem(
          "rs.triage.seen",
          JSON.stringify(Array.from(seenRef.current))
        );
      } catch (_) {
        /* silent */
      }
      return;
    }
    const freshList = [];
    for (const r of recent) {
      if (seenRef.current.has(r.id)) continue;
      seenRef.current.add(r.id);
      if (dismissedRef.current.has(r.id)) continue;
      const unassigned =
        !r.category || r.category === "other" || r.category === "unassigned";
      const potentiallyPersonal =
        r.tax_deductible === false || r.personal_suspicion === true;
      if (unassigned || potentiallyPersonal) freshList.push(r);
    }
    try {
      window.localStorage.setItem(
        "rs.triage.seen",
        JSON.stringify(Array.from(seenRef.current))
      );
    } catch (_) {
      /* silent */
    }
    if (freshList.length > 0) {
      // Route both intake streams through the shared enqueueCandidate branch so
      // the automated fresh-receipts effect and the manual event bridge cannot
      // drift apart. Each successive receipt is fed through the same helper —
      // it starts the modal on the first call and stacks the rest into queue.
      let currentCandidate = candidate;
      for (const r of freshList) {
        enqueueCandidate({
          receipt: r,
          candidate: currentCandidate,
          setCandidate,
          setQueue,
        });
        currentCandidate = currentCandidate || r; // first fresh becomes the head
      }
    }
  }, [recent, candidate]);

  useEffect(() => {
    const onOpen = (e) => enqueue(e?.detail);
    window.addEventListener("loophole-triage-open", onOpen);
    return () => window.removeEventListener("loophole-triage-open", onOpen);
  }, [enqueue]);

  const advance = () => {
    setCandidate(null);
    setQueue((prev) => {
      if (prev.length === 0) return prev;
      const [next, ...rest] = prev;
      // Defer so the modal actually unmounts+remounts for the exit/enter animation
      setTimeout(() => setCandidate(next), 60);
      return rest;
    });
  };

  const applyClassification = async (rid, classification) => {
    if (classification === "business") {
      try {
        await api.patch(`/receipts/${rid}`, { tax_deductible: true });
      } catch (_) {
        /* silent */
      }
    } else if (classification === "personal") {
      try {
        await api.patch(`/receipts/${rid}`, { tax_deductible: false });
      } catch (_) {
        /* silent */
      }
    }
  };

  const dismiss = (rid, classification) => {
    dismissedRef.current.add(rid);
    try {
      window.localStorage.setItem(
        "rs.triage.dismissed",
        JSON.stringify(Array.from(dismissedRef.current))
      );
    } catch (_) {
      /* silent */
    }
    advance();
    if (classification === "business" || classification === "personal") {
      applyClassification(rid, classification);
      const label =
        classification === "business"
          ? "Marked as business — added to Schedule C."
          : "Marked personal — excluded from deductions.";
      const undoClassification =
        classification === "business" ? "personal" : "business";
      (classification === "business" ? toast.success : toast.info)(label, {
        duration: 5000,
        id: `triage-${rid}`,
        description: "You have 5 seconds to undo.",
        action: {
          label: "Undo",
          onClick: () => {
            applyClassification(rid, undoClassification);
            toast.message("Undone — you can re-classify this receipt anytime.", {
              duration: 2500,
            });
          },
        },
      });
    }
  };

  if (!candidate) return null;
  const kw = matchBusinessKeyword(candidate.merchant);

  return (
    <div
      data-testid="loophole-triage-modal"
      className="fixed inset-0 z-[9600] flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-0"
      style={{ background: "rgba(2,6,23,0.55)", backdropFilter: "blur(4px)" }}
      onClick={() => dismiss(candidate.id, null)}
    >
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="glass rounded-2xl w-full max-w-md p-6 relative"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,23,42,0.96), rgba(2,6,23,0.98))",
          border: "1px solid rgba(16,185,129,0.4)",
          boxShadow: "0 24px 50px -12px rgba(16,185,129,0.45)",
        }}
      >
        <button
          onClick={() => dismiss(candidate.id, null)}
          data-testid="loophole-triage-close"
          className="absolute top-3 right-3 text-slate-400 hover:text-white p-1 rounded"
          aria-label="Close"
        >
          <X size={16} />
        </button>
        <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-emerald-400 mb-2 flex items-center gap-1.5">
          <Sparkles size={11} /> Loophole Triage · New Receipt
          {queue.length > 0 && (
            <span
              data-testid="triage-queue-counter"
              className="ml-2 px-2 py-0.5 rounded-full text-emerald-200"
              style={{
                background: "rgba(16,185,129,0.15)",
                border: "1px solid rgba(16,185,129,0.45)",
                fontSize: 9,
              }}
              title={`${queue.length} more receipt${queue.length === 1 ? "" : "s"} waiting for triage`}
            >
              +{queue.length} queued
            </span>
          )}
        </div>
        <div className="text-white font-bold text-lg tracking-tight leading-tight mb-1">
          {candidate.merchant}
        </div>
        <div className="text-slate-400 text-sm mb-4 flex items-center gap-2">
          <span className="font-mono">{candidate.date}</span>
          <span>·</span>
          <span className="font-mono text-emerald-300">
            ${Number(candidate.total || 0).toFixed(2)}
          </span>
        </div>

        <div className="text-slate-300 text-sm mb-4">
          How should we file this one?
        </div>

        <div className="flex gap-3 mb-4">
          <button
            onClick={() => dismiss(candidate.id, "business")}
            data-testid="loophole-triage-business"
            className="flex-1 h-11 rounded-lg font-semibold text-sm text-emerald-100 flex items-center justify-center gap-2 transition-transform hover:-translate-y-px"
            style={{
              background:
                "linear-gradient(135deg, rgba(16,185,129,0.22), rgba(16,185,129,0.08))",
              border: "1px solid rgba(16,185,129,0.55)",
              boxShadow: "0 12px 28px -12px rgba(16,185,129,0.6)",
            }}
          >
            <ShieldCheck size={14} /> Business Purchase
          </button>
          <button
            onClick={() => dismiss(candidate.id, "personal")}
            data-testid="loophole-triage-personal"
            className="flex-1 h-11 rounded-lg font-semibold text-sm text-slate-200 flex items-center justify-center gap-2 transition-transform hover:-translate-y-px"
            style={{
              background:
                "linear-gradient(135deg, rgba(148,163,184,0.15), rgba(148,163,184,0.05))",
              border: "1px solid rgba(148,163,184,0.35)",
            }}
          >
            <User size={14} /> Personal Expense
          </button>
        </div>

        {/* AI Insight Prompt Hook — pulses gold when we spot a common deductible pattern */}
        <div
          data-testid="loophole-ai-insight"
          data-insight-active={!!kw}
          className={`rounded-xl p-3.5 text-xs leading-relaxed ${kw ? "loophole-insight-pulse" : ""}`}
          style={{
            border: `1px solid ${kw ? "rgba(245,158,11,0.55)" : "rgba(148,163,184,0.2)"}`,
            background: kw
              ? "linear-gradient(135deg, rgba(245,158,11,0.14), rgba(245,158,11,0.04))"
              : "rgba(148,163,184,0.05)",
          }}
        >
          <div
            className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.22em] mb-1"
            style={{ color: kw ? "#F59E0B" : "#94a3b8" }}
          >
            <Sparkles size={10} />
            {kw ? `AI Insight · ${kw.tag}` : "AI Insight"}
          </div>
          <div className={kw ? "text-amber-100" : "text-slate-400"}>
            {kw
              ? kw.tip
              : "No obvious deductible pattern detected. Choose based on how this purchase was used for work."}
          </div>
        </div>
        <style>{`
          .loophole-insight-pulse {
            animation: loophole-gold-pulse 2.6s ease-in-out infinite;
          }
          @keyframes loophole-gold-pulse {
            0%, 100% {
              box-shadow: 0 0 0 1px rgba(245,158,11,0.25),
                          0 0 20px -6px rgba(245,158,11,0.4);
            }
            50% {
              box-shadow: 0 0 0 1px rgba(245,158,11,0.55),
                          0 0 32px -4px rgba(245,158,11,0.75);
            }
          }
        `}</style>
      </motion.div>
    </div>
  );
}

// Backwards-compatible alias — several callers still import the old name.
export const LoopholeTriageWidget = LoopholeTriageModal;
